import { useState } from 'react';
import { useBrands } from '../../../context/BrandsContext';

export default function ProductCategories() {
  const [selectedBrand, setSelectedBrand] = useState('Mégane 3');
  const navigate = useNavigate();
  const { brands, loading } = useBrands();

  const currentBrand = brands.find(b => b.name === selectedBrand) || brands[0];

  if (loading || !currentBrand) return null;

  return (
    <section id="products" className="bg-white py-16 md:py-32">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-8">
        {/* Section Header */}
        <div className="mb-10 md:mb-16 text-center">
          <div className="flex items-center justify-center gap-4 mb-4 md:mb-6">
            <div className="w-10 md:w-16 h-[2px] bg-gradient-to-r from-transparent via-black to-transparent"></div>
            <span className="text-gray-500 text-xs uppercase tracking-[3px] md:tracking-[4px] font-bold">
              PRECISION ENGINEERED
            </span>
            <div className="w-10 md:w-16 h-[2px] bg-gradient-to-r from-transparent via-black to-transparent"></div>
          </div>
          <h2 className="text-black text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter leading-[1.1] mb-4 md:mb-6">
            SHOP BY BRAND
          </h2>
          <p className="text-gray-600 text-base md:text-xl max-w-3xl mx-auto mb-6 md:mb-8">
            Carbon fiber components engineered for each model — perfect fitment and maximum performance gains.
          </p>
          <button
            onClick={() => navigate('/shop')}
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gray-500 hover:text-black transition-colors cursor-pointer group"
          >
            Browse full shop
            <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
          </button>
        </div>

        {/* Brand Selector Tabs */}
        <div className="flex items-center justify-center gap-2 md:gap-3 mb-10 md:mb-20 flex-wrap">
          {brands.map((brand) => (
            <button
              key={brand.name}
              onClick={() => setSelectedBrand(brand.name)}
              className={`px-5 md:px-10 py-3 md:py-4 text-xs md:text-base font-bold uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer ${
                selectedBrand === brand.name
                  ? 'bg-black text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {brand.name}
            </button>
          ))}
        </div>

        {/* Brand Content - Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 mb-16 md:mb-32 items-center">
          {/* Left Side - Brand Info */}
          <div className="space-y-6 md:space-y-8">
            <div>
              <h3 className="text-black text-3xl sm:text-4xl md:text-6xl font-black tracking-tighter leading-[1.1] mb-3 md:mb-6">
                {currentBrand.fullName}
              </h3>
              <p className="text-gray-600 text-base md:text-lg mb-4 md:mb-8">
                Precision-engineered carbon fiber components designed specifically for your model.
              </p>
            </div>

            {/* Model List */}
            <div>
              <h4 className="text-black text-xs uppercase tracking-[3px] font-bold mb-3 md:mb-4">
                COMPATIBLE MODELS
              </h4>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                {currentBrand.models.map((model, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 px-3 md:px-4 py-2 md:py-3 text-gray-700 text-xs md:text-sm font-semibold border-l-2 border-black"
                  >
                    {model}
                  </div>
                ))}
              </div>
            </div>

            {/* Part Categories as Pills */}
            <div>
              <h4 className="text-black text-xs uppercase tracking-[3px] font-bold mb-3 md:mb-4">
                PART CATEGORIES
              </h4>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {currentBrand.categories.map((category, index) => (
                  <button
                    key={index}
                    onClick={() => navigate(`/shop/${currentBrand.slug}`)}
                    className="px-3 md:px-5 py-1.5 md:py-2 bg-white border border-gray-300 text-gray-700 text-xs md:text-sm font-medium hover:border-black hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* CTA Button */}
            <div className="pt-2 md:pt-4">
              <button
                onClick={() => navigate(`/shop/${currentBrand.slug}`)}
                className="bg-black px-8 md:px-12 py-4 md:py-5 text-white text-sm md:text-base font-bold uppercase tracking-wider hover:bg-gray-800 transition-all whitespace-nowrap cursor-pointer group flex items-center gap-3 w-full sm:w-auto justify-center sm:justify-start"
              >
                SHOP {currentBrand.name} PARTS
                <i className="ri-arrow-right-line text-lg md:text-xl group-hover:translate-x-2 transition-transform"></i>
              </button>
            </div>
          </div>

          {/* Right Side - Product Image */}
          <div className="relative group order-first lg:order-last">
            <div className="relative bg-gray-50 p-6 md:p-12 overflow-hidden">
              <div className="w-full h-[280px] sm:h-[380px] md:h-[600px] flex items-center justify-center">
                <img
                  src={currentBrand.brandImage}
                  alt={currentBrand.fullName}
                  loading="lazy"
                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700 cursor-pointer"
                  onClick={() => navigate(`/shop/${currentBrand.slug}`)}
                />
              </div>
              {/* Corner Accent */}
              <div className="absolute top-0 right-0 w-0 h-0 border-t-[40px] md:border-t-[60px] border-t-black border-l-[40px] md:border-l-[60px] border-l-transparent"></div>
              <div className="absolute bottom-0 left-0 w-0 h-0 border-b-[40px] md:border-b-[60px] border-b-black border-r-[40px] md:border-r-[60px] border-r-transparent"></div>
            </div>
          </div>
        </div>

        {/* Feature Strip - 3 Columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-8">
          <div className="relative group bg-gray-50 p-6 md:p-10 border-t-4 border-black">
            <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-black mb-4 md:mb-6">
              <i className="ri-scan-line text-white text-2xl md:text-3xl"></i>
            </div>
            <h3 className="text-black text-xl md:text-2xl font-black mb-3 md:mb-4 uppercase tracking-tight">
              OEM Fitment
            </h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              3D-scanned precision engineering ensures perfect fitment without modifications. Every component is designed using CAD data from original equipment specifications.
            </p>
          </div>

          <div className="relative group bg-gray-50 p-6 md:p-10 border-t-4 border-black">
            <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-black mb-4 md:mb-6">
              <i className="ri-temp-cold-line text-white text-2xl md:text-3xl"></i>
            </div>
            <h3 className="text-black text-xl md:text-2xl font-black mb-3 md:mb-4 uppercase tracking-tight">
              Dry Carbon
            </h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              We use exclusively dry carbon technology — the highest quality process for manufacturing carbon fiber parts. The result is superior strength, minimum weight, and a flawless finish.
            </p>
          </div>

          <div className="relative group bg-gray-50 p-6 md:p-10 border-t-4 border-black">
            <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-black mb-4 md:mb-6">
              <i className="ri-flag-line text-white text-2xl md:text-3xl"></i>
            </div>
            <h3 className="text-black text-xl md:text-2xl font-black mb-3 md:mb-4 uppercase tracking-tight">
              Track Proven
            </h3>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed">
              Tested on professional race circuits including Nürburgring and validated by motorsport teams. Performance and durability you can trust at any speed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
